import supabase from './supabaseClient';

/**
 * Authentication utility functions for the filter tool
 * Follows the human-in-the-loop design principle by keeping users in control
 * of their authentication and sessions
 * 
 * Token refresh and secure session management are implemented to ensure
 * a seamless user experience while maintaining security.
 */

/**
 * Sign in with email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise} Result of the sign in attempt
 */
export const signInWithPassword = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error signing in with password:', error.message);
    return { data: null, error };
  }
};

/**
 * Sign in with Google
 * @returns {Promise} Result of the sign in attempt
 */
export const signInWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error signing in with Google:', error.message);
    return { data: null, error };
  }
};

/**
 * Sign out the current user and clear all application state data
 * @returns {Promise} Result of the sign out attempt
 */
export const signOut = async () => {
  try {
    // Before signing out, clear all filter tool related localStorage data for a fresh start
    // This ensures a clean state when the user logs back in
    console.log('Clearing all filter tool data for a fresh start...');
    
    // Clear filter tool state data
    localStorage.removeItem('filterToolState');
    localStorage.removeItem('filterToolExpandedStates');
    
    // Clear any other application specific data
    // Add any other app-specific localStorage keys that should be cleared
    
    // Now perform the actual sign out
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    // Return success
    return { error: null };
  } catch (error) {
    console.error('Error signing out:', error.message);
    return { error };
  }
};

/**
 * Get the current session
 * @returns {Promise} The current session
 */
export const getCurrentSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return { session: data.session, error: null };
  } catch (error) {
    console.error('Error getting session:', error.message);
    return { session: null, error };
  }
};

/**
 * Get the current user
 * @returns {Promise} The current user
 */
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return { user: data.user, error: null };
  } catch (error) {
    console.error('Error getting user:', error.message);
    return { user: null, error };
  }
};

/**
 * Set up auth state change listener
 * @param {Function} callback Function to call when auth state changes
 * @returns {Function} Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  return supabase.auth.onAuthStateChange((event, session) => {
    console.log('Auth state changed:', event);
    callback(event, session);
  });
};

/**
 * Manually refresh the session token
 * @returns {Promise} Result of the refresh attempt
 */
export const refreshSession = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    return { session: data.session, user: data.user, error: null };
  } catch (error) {
    console.error('Error refreshing session:', error.message);
    return { session: null, user: null, error };
  }
};

/**
 * Get user profile data from Supabase
 * @param {string} userId The user ID to fetch profile for
 * @returns {Promise} The user profile data
 */
export const getUserProfile = async (userId) => {
  if (!userId) return { profile: null, error: new Error('User ID is required') };
  
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
      
    if (error) throw error;
    return { profile: data, error: null };
  } catch (error) {
    console.error('Error fetching user profile:', error.message);
    return { profile: null, error };
  }
};
